import { useMemo, useState } from 'react';

import { Add } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

import {
  useDeleteSavedRouteAlertMutation,
  useGetSavedRouteAlertsQuery,
} from 'state/emailAlertsSlices';
import { useAppSelector } from 'state/hooks';
import {
  useGetProjectQuery,
  useGetProjectSettingQuery,
  useUpdateProjectMutation,
  useUpdateProjectSettingMutation,
} from 'state/projectSlices';
import { useGetSavedRoutesQuery } from 'state/savedRouteSlices';
import { getProjectInfoForSlug, selectUserState } from 'state/userSlice';

import { StlButton } from 'features/common/StlButton/StlButton';
import { secondary_light_text_color } from 'theme/cemTheme';

import { AlertCard } from './AlertCard';
import { AlertListItem } from './AlertListItem';
import { NotificationsPanel } from './NotificationsPanel';
import { filterAlerts, formatAlertName } from './alertHelpers';
import { DeleteAlertPopup } from './components/DeleteAlertPopup';
import { NoAlerts, NoResults, NoRoutes } from './components/EmptyState';
import { FilterSearchBar } from './components/FilterSearchBar';
import { useNotificationProject } from './useNotificationProject';

import styles from './ManageAlerts.module.css';

export function ManageAlerts() {
  const { projectSlug, navigateTo } = useNotificationProject();

  const [deleteAlert] = useDeleteSavedRouteAlertMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteAlertId, setDeleteAlertId] = useState<string | null>(null);
  const userState = useAppSelector(selectUserState);

  const projectInfo = getProjectInfoForSlug(userState, projectSlug);
  const projectSettingsId = projectInfo?.id;

  const isSubAdmin = projectInfo?.is_sub_admin || false;

  const {
    data: alerts,
    isLoading,
    error,
  } = useGetSavedRouteAlertsQuery(projectSlug!, { skip: !projectSlug });

  const { currentData: savedRouteData, isLoading: isLoadingRoutes } =
    useGetSavedRoutesQuery(projectSlug!, {
      skip: !projectSlug,
    });

  const { data: projectSetting, isLoading: isLoadingProjectSetting } =
    useGetProjectSettingQuery(projectSettingsId, {
      skip: !projectSettingsId,
    });

  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery(
    projectSlug!,
    { skip: !projectSlug },
  );

  const [updateProjectSetting] = useUpdateProjectSettingMutation();
  const [updateProject] = useUpdateProjectMutation();

  const receiveEmailAlerts = projectSetting?.are_alerts_enabled ?? false;
  const enableAlertsForProject = project?.are_alerts_enabled ?? false;

  const handleReceiveEmailAlertsChange = (checked: boolean) => {
    if (!projectSetting?.id) {
      return;
    }
    updateProjectSetting({
      id: projectSetting.id,
      data: {
        are_alerts_enabled: checked,
      },
    });
  };

  const handleEnableAlertsForProjectChange = (checked: boolean) => {
    if (!projectSlug) return;
    updateProject({
      project_slug: projectSlug,
      data: { are_alerts_enabled: checked },
    });
  };

  const filteredAlerts = useMemo(() => {
    if (!alerts) return [];
    return filterAlerts(alerts, searchQuery);
  }, [alerts, searchQuery]);

  const hasAnyAlerts = (alerts?.length ?? 0) > 0;
  const hasAnyRoutes = savedRouteData?.savedRoutes?.length > 0;

  const handleDeleteClick = (alertId: string) => {
    setDeleteAlertId(alertId);
  };

  const handleAlertClick = (alertId: string) => {
    navigateTo.viewAlert(alertId);
  };

  const handleDeleteConfirm = () => {
    if (!deleteAlertId || !projectSlug) return;

    deleteAlert({
      project_slug: projectSlug,
      alert_id: deleteAlertId,
    });
    setDeleteAlertId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteAlertId(null);
  };

  const alertToDelete = useMemo(() => {
    if (!deleteAlertId || !alerts) return null;
    return alerts.find((a) => a.hash_id === deleteAlertId);
  }, [deleteAlertId, alerts]);

  const alertToDeleteRouteNames = useMemo(() => {
    return alertToDelete?.routes.map((route) => route.name) || [];
  }, [alertToDelete]);

  const handleEdit = (alertId: string) => {
    navigateTo.editAlert(alertId);
  };

  return (
    <NotificationsPanel>
      <Stack spacing="0.5rem" height="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h3"
            fontWeight={700}
            color={secondary_light_text_color}
          >
            Manage Alerts
          </Typography>
          {enableAlertsForProject && hasAnyRoutes && (
            <StlButton
              isActive
              value="Create Alert"
              handleClick={() => navigateTo.newAlert()}
              icon={<Add />}
              width="7.5rem"
            />
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} gap="0.5rem">
          <AlertCard
            title="Receive email alerts"
            description="Get emails for alerts you're assigned to. Check an alert in the list below to see or edit the recipients"
            checked={receiveEmailAlerts}
            onChange={handleReceiveEmailAlertsChange}
            disabled={isLoadingProjectSetting}
          />
          {isSubAdmin && (
            <AlertCard
              title="Enable alerts for this project"
              description="All users can create alerts and get emails for this project when enabled"
              checked={enableAlertsForProject}
              onChange={handleEnableAlertsForProjectChange}
              disabled={isLoadingProject}
            />
          )}
        </Stack>
        <FilterSearchBar
          searchTerm={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by alert details, route name, or recipient email"
          hasFrame
        />
        {!isLoading &&
          !isLoadingRoutes &&
          !error &&
          (filteredAlerts.length === 0 ? (
            <Stack flex={1} minHeight={0}>
              {hasAnyAlerts ? (
                <NoResults resourceName="alerts" />
              ) : !hasAnyRoutes ? (
                <NoRoutes />
              ) : (
                <NoAlerts />
              )}
            </Stack>
          ) : (
            <Stack className={styles.alertsListContainer} spacing="0.5rem">
              {filteredAlerts.map((alert) => (
                <AlertListItem
                  key={alert.hash_id}
                  alert={alert}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onClick={handleAlertClick}
                />
              ))}
            </Stack>
          ))}
      </Stack>
      {alertToDelete && (
        <DeleteAlertPopup
          isOpen={!!alertToDelete}
          alertName={formatAlertName(
            alertToDelete.metric_type,
            alertToDelete.metric_criteria,
          )}
          routes={alertToDeleteRouteNames}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </NotificationsPanel>
  );
}
